import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import PlayerAvatar from "@/components/PlayerAvatar";
import { DAY_NAMES } from "@/pages/Coaching";

const EMPTY = { name: "", bio: "", photo_url: "", specialties: "", price_per_session: "1500", available_days: [0, 1, 2, 3, 4, 5], available_hours: "08:00-18:00", is_active: true };

export default function AdminCoaches() {
  const [coaches, setCoaches] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => { api.get("/admin/coaches").then(r => setCoaches(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setDialog("new"); };
  const openEdit = (c) => { setForm({ ...c, specialties: (c.specialties || []).join(", "), price_per_session: String(c.price_per_session) }); setDialog(c); };
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggleDay = (d) => setForm(f => ({ ...f, available_days: f.available_days.includes(d) ? f.available_days.filter(x => x !== d) : [...f.available_days, d].sort() }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Coach name is required"); return; }
    const payload = { name: form.name, bio: form.bio, photo_url: form.photo_url, price_per_session: parseFloat(form.price_per_session) || 0,
      specialties: form.specialties.split(",").map(s => s.trim()).filter(Boolean), available_days: form.available_days, available_hours: form.available_hours, is_active: form.is_active !== false };
    try {
      if (dialog === "new") await api.post("/coaches", payload); else await api.put(`/coaches/${dialog._id}`, payload);
      toast.success("Coach saved"); setDialog(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name}?`)) return;
    try { await api.delete(`/coaches/${c._id}`); toast.success("Coach removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="border-border/50" data-testid="admin-coaches">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-lg">Coaching staff ({coaches.length})</CardTitle>
        <Button size="sm" onClick={openNew} className="rounded-full bg-coral hover:bg-coral-600 text-white gap-1" data-testid="admin-add-coach"><Plus size={14} /> Add coach</Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {coaches.map(c => (
          <div key={c._id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/20 border border-border/30" data-testid="admin-coach-row">
            <PlayerAvatar name={c.name} avatarUrl={c.photo_url} size="sm" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm flex items-center gap-2">{c.name} {!c.is_active && <Badge variant="outline" className="rounded-full text-[9px]">hidden</Badge>}</p>
              <p className="text-[11px] text-muted-foreground truncate">Rs {c.price_per_session} · {(c.available_days || []).map(d => DAY_NAMES[d]).join(" ")} · {c.available_hours}{c.specialties?.length ? ` · ${c.specialties.join(", ")}` : ""}</p>
            </div>
            <Button size="icon" variant="ghost" className="w-7 h-7 rounded-full" onClick={() => openEdit(c)} data-testid={`edit-coach-${c._id}`}><Pencil size={12} /></Button>
            <Button size="icon" variant="ghost" className="w-7 h-7 rounded-full text-destructive" onClick={() => remove(c)} data-testid={`delete-coach-${c._id}`}><Trash2 size={12} /></Button>
          </div>
        ))}
        {coaches.length === 0 && <p className="text-sm text-muted-foreground text-center py-10">No coaches yet. Add your staff so players can book lessons.</p>}
      </CardContent>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{dialog === "new" ? "Add coach" : "Edit coach"}</DialogTitle><DialogDescription>Lessons are 60 minutes on Central Court and block the court slot.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={set("name")} className="rounded-xl" data-testid="coach-name-input" /></div>
              <div><Label>Price / lesson (Rs)</Label><Input type="number" min="0" value={form.price_per_session} onChange={set("price_per_session")} className="rounded-xl" data-testid="coach-price-input" /></div>
            </div>
            <div><Label>Bio</Label><Textarea value={form.bio} onChange={set("bio")} className="rounded-xl min-h-[60px]" placeholder="Experience, certifications, coaching style..." data-testid="coach-bio-input" /></div>
            <div><Label>Specialties <span className="text-[10px] text-muted-foreground font-normal">(comma separated)</span></Label><Input value={form.specialties} onChange={set("specialties")} className="rounded-xl" placeholder="Beginners, Tactics, Kids" data-testid="coach-specialties-input" /></div>
            <div><Label>Photo URL</Label><Input value={form.photo_url} onChange={set("photo_url")} className="rounded-xl" placeholder="https://..." /></div>
            <div>
              <Label className="mb-2 block">Available days</Label>
              <div className="flex gap-1.5 flex-wrap">
                {DAY_NAMES.map((d, i) => (
                  <button key={d} type="button" onClick={() => toggleDay(i)} className={`px-3 py-1 rounded-full text-xs font-medium border ${form.available_days.includes(i) ? "bg-teal/10 text-teal border-teal/30" : "bg-muted/30 text-muted-foreground border-border/30"}`} data-testid={`coach-day-${i}`}>{d}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div><Label>Hours (HH:MM-HH:MM)</Label><Input value={form.available_hours} onChange={set("available_hours")} className="rounded-xl" placeholder="08:00-18:00" data-testid="coach-hours-input" /></div>
              <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={form.is_active !== false} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} data-testid="coach-active-checkbox" /> Visible to players</label>
            </div>
            <Button onClick={save} className="w-full rounded-full bg-coral hover:bg-coral-600 text-white" data-testid="save-coach-btn">Save coach</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
